/**
 * Tiers Management Page
 * View tier configurations, customer leaderboard, and tier analytics
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui';
import { TierBadge } from '../components/ui/TierBadge';
import { businessApi } from '../lib/api';

type TierLevel = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | 'LEGEND_DERIVED';

interface TierConfig {
  tier: TierLevel;
  displayName: string;
  requiredStars: number;
  earnMultiplier: number;
  redeemDiscount: number;
  color: string;
  icon: string;
  benefits: string[];
}

// Mirrors backend/src/config/buffables.ts — keep in sync.
const TIER_CONFIGS: TierConfig[] = [
  {
    tier: 'BRONZE',
    displayName: 'Buffies Member',
    requiredStars: 0,
    earnMultiplier: 1.0,
    redeemDiscount: 0,
    color: '#FF6A39',
    icon: '⭐',
    benefits: [
      '$5 off your next order',
      'Free birthday drink (excluding smoothies)',
      'Entry to monthly giveaway',
      '1 Star per $1 spent',
    ],
  },
  {
    tier: 'SILVER',
    displayName: 'Buffies Crew',
    requiredStars: 100,
    earnMultiplier: 1.2,
    redeemDiscount: 0,
    color: '#C0C0C0',
    icon: '🥈',
    benefits: [
      '1 free 16oz drink',
      '1 free order of waffles',
      '1.2× Stars per $1 spent',
    ],
  },
  {
    tier: 'GOLD',
    displayName: 'Buffies VIP',
    requiredStars: 400,
    earnMultiplier: 1.4,
    redeemDiscount: 0,
    color: '#FFD700',
    icon: '🥇',
    benefits: [
      'Free hat or shirt',
      '1 free 16oz drink',
      '1 free toast',
      '1.4× Stars per $1 spent',
    ],
  },
  {
    tier: 'PLATINUM',
    displayName: 'Buffies Icon',
    requiredStars: 700,
    earnMultiplier: 1.6,
    redeemDiscount: 0,
    color: '#E5E4E2',
    icon: '💎',
    benefits: [
      'Free hat or shirt',
      '2 free 16oz drinks',
      '1 free 16oz smoothie',
      '1.6× Stars per $1 spent',
    ],
  },
  {
    tier: 'LEGEND_DERIVED',
    displayName: 'Buffies Legend',
    requiredStars: 1600,
    earnMultiplier: 1.8,
    redeemDiscount: 0,
    color: '#051C2C',
    icon: '👑',
    benefits: [
      '10% off menu items',
      '2 free drinks',
      '2 free items of choice',
      'Free drip coffee + teas rest of year',
      'Invitations to seasonal menu tastings',
      '1.8× Stars per $1 spent',
    ],
  },
];

export const TiersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'leaderboard'>('overview');
  const [tiersData, setTiersData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTiers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await businessApi.getTiers();
      setTiersData(response.data.data);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load tier data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTiers();
  }, []);

  const topCustomers = tiersData?.topCustomers ?? [];
  const tierDist = tiersData?.tierDistribution ?? { BRONZE: 0, SILVER: 0, GOLD: 0, PLATINUM: 0, total: 0 };

  return (
    <div className="flex flex-col gap-8">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadTiers} className="ml-4 px-3 py-1 text-sm font-medium text-red-700 border border-red-300 rounded-md hover:bg-red-100 transition-colors">Retry</button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">Loyalty Tiers</h1>
          <p className="text-base mt-2 text-black-lighter">
            Manage your tiered loyalty program and track top customers
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="inline-flex rounded-lg p-1 self-start bg-cream-100 border border-border-light">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
            activeTab === 'overview'
              ? 'bg-white text-black shadow-sm'
              : 'bg-transparent text-black-lighter hover:text-black'
          }`}
        >
          Tier Overview
        </button>
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
            activeTab === 'leaderboard'
              ? 'bg-white text-black shadow-sm'
              : 'bg-transparent text-black-lighter hover:text-black'
          }`}
        >
          Leaderboard
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {TIER_CONFIGS.map((config) => (
            <Card key={config.tier}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${config.color}20` }}
                    >
                      <span className="text-3xl">{config.icon}</span>
                    </div>
                    <div>
                      <TierBadge tier={config.tier} size="lg" />
                      <p className="text-sm text-black-lighter mt-1">
                        {config.requiredStars === 0
                          ? 'Starting tier'
                          : `${config.requiredStars.toLocaleString()} Stars lifetime earned`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Multiplier Info */}
                <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-cream-50 rounded-lg">
                  <div>
                    <p className="text-xs font-bold uppercase text-black-lighter mb-1">
                      Earn Multiplier
                    </p>
                    <p className="text-h4 font-bold text-orange-400">
                      {config.earnMultiplier}x
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-black-lighter mb-1">
                      Redemption Discount
                    </p>
                    <p className="text-h4 font-bold text-green-500">
                      {(config.redeemDiscount * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>

                {/* Benefits */}
                <div>
                  <p className="text-sm font-semibold text-black mb-2">Benefits:</p>
                  <ul className="space-y-2">
                    {config.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span className="text-sm text-black-light flex-1">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div>
          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Icon / Legend', value: isLoading ? '...' : String(tierDist.PLATINUM), icon: '💎', color: 'bg-purple-50 text-purple-600' },
              { label: 'VIP', value: isLoading ? '...' : String(tierDist.GOLD), icon: '🥇', color: 'bg-yellow-50 text-yellow-600' },
              { label: 'Crew', value: isLoading ? '...' : String(tierDist.SILVER), icon: '🥈', color: 'bg-gray-50 text-gray-600' },
              { label: 'Member', value: isLoading ? '...' : String(tierDist.BRONZE), icon: '⭐', color: 'bg-orange-50 text-orange-600' },
            ].map((stat) => (
              <Card key={stat.label}>
                <div className="p-5">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                      <span className="text-xl">{stat.icon}</span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-black-lighter tracking-wide">
                        {stat.label}
                      </p>
                      <p className="text-h4 font-bold text-black">{stat.value}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Top Customers */}
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">Top Customers</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Your most loyal customers by lifetime Stars earned
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-cream-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                      Rank
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                      Customer
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                      Tier
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                      Lifetime Earned
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                      Balance
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                      Check-ins
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                      Streak
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-light">
                  {isLoading ? (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-black-lighter text-sm">Loading...</td></tr>
                  ) : topCustomers.length === 0 ? (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-black-lighter text-sm">No customer data yet.</td></tr>
                  ) : topCustomers.map((customer: any, index: number) => (
                    <tr key={customer.id} className="hover:bg-cream-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {index === 0 && <span className="text-xl">🥇</span>}
                          {index === 1 && <span className="text-xl">🥈</span>}
                          {index === 2 && <span className="text-xl">🥉</span>}
                          <span className="text-base font-semibold text-black">{index + 1}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-base font-medium text-black">{customer.name}</p>
                          <p className="text-sm text-black-lighter">{customer.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <TierBadge
                          tier={customer.tier}
                          size="sm"
                          label={customer.tier === 'PLATINUM' && customer.lifetimeEarned >= 1600 ? 'Buffies Legend' : undefined}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-base font-semibold text-black">
                          {customer.lifetimeEarned.toLocaleString()} Stars
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-base text-black">{customer.b3trBalance.toLocaleString()}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-base text-black">{customer.totalCheckIns}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <span className="text-orange-400">🔥</span>
                          <span className="text-base font-semibold text-black">
                            {customer.streakDays}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TiersPage;
